// UserMenu Component - User Dropdown Menu

import React, { useEffect, useRef, useState } from 'react';
import { Heart, Plus, Shield, LogIn, LogOut, User, Crown, Book, Users, Settings, Download, Share, MessageCircle, UsersRound, MapPin, BookOpen, Info, ChevronDown, ChevronUp, FileText, Scale } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { usePWA } from '../../hooks';

interface UserMenuProps {
  onClose: () => void;
  onNavigate: (view: string) => void;
  isModerator?: boolean;
  isAdmin?: boolean;
  favoritesCount?: number;
  pendingCount?: number;
}

export function UserMenu({
  onClose,
  onNavigate,
  isModerator = false,
  isAdmin = false,
  favoritesCount = 0,
  pendingCount = 0,
}: UserMenuProps) {
  const { user, signOut } = useAuth();
  const menuRef = useRef<HTMLDivElement>(null);
  const { isMobile, isInstallable, isInstalled, isIOS, promptInstall } = usePWA();
  const [showAbout, setShowAbout] = useState(false);

  // Close on click outside - use click instead of mousedown for better mobile support
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click is outside the menu
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Small delay to prevent the menu from closing immediately on the same click that opened it
    // This is especially important for touch devices where click events can bubble unexpectedly
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [onClose]);

  const handleAction = (action: string, event?: React.MouseEvent) => {
    // Stop propagation to prevent event bubbling on mobile
    event?.stopPropagation();
    event?.preventDefault();

    // Navigate first, then close menu with small delay for mobile
    onNavigate(action);
    // Small delay to ensure navigation state is set before menu closes
    setTimeout(() => {
      onClose();
    }, 10);
  };

  const handleSignOut = async () => {
    await signOut();
    onClose();
  };

  return (
    <div
      ref={menuRef}
      className="absolute right-0 top-12 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50 animate-fade-in"
    >
      {user ? (
        <>
          {/* User Info */}
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="font-semibold text-gray-900 truncate">
              {user.displayName || user.email}
            </p>
            <p className="text-xs text-gray-500 truncate">{user.email}</p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={(e) => handleAction('favorites', e)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <Heart className="w-4 h-4" />
              <span>Meine Favoriten</span>
              {favoritesCount > 0 && (
                <span className="ml-auto bg-indigo-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {favoritesCount}
                </span>
              )}
            </button>

            <button
              onClick={(e) => handleAction('create', e)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <Plus className="w-4 h-4" />
              <span>Ort vorschlagen</span>
            </button>

            <button
              onClick={(e) => handleAction('myPlaces', e)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <MapPin className="w-4 h-4" />
              <span>Meine Orte</span>
            </button>

            <button
              onClick={(e) => handleAction('logbook', e)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <Book className="w-4 h-4" />
              <span>Mein Logbook</span>
            </button>

            <button
              onClick={(e) => handleAction('messages', e)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <MessageCircle className="w-4 h-4" />
              <span>Nachrichten</span>
            </button>

            <button
              onClick={(e) => handleAction('community', e)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <Users className="w-4 h-4" />
              <span>Community</span>
            </button>

            <button
              onClick={(e) => handleAction('groups', e)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <UsersRound className="w-4 h-4" />
              <span>Gruppen</span>
            </button>

            <button
              onClick={(e) => handleAction('magazine', e)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <BookOpen className="w-4 h-4" />
              <span>Magazin</span>
            </button>

            <button
              onClick={(e) => handleAction('privacySettings', e)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <Settings className="w-4 h-4" />
              <span>Privatsphare</span>
            </button>

            {isMobile && isInstallable && !isInstalled && (
              <button
                onClick={() => {
                  if (isIOS) {
                    // iOS doesn't support programmatic install
                    alert('Tippe auf "Teilen" und dann auf "Zum Home-Bildschirm", um die App zu installieren.');
                  } else {
                    promptInstall();
                  }
                  onClose();
                }}
                className="w-full px-4 py-2 text-left text-sm text-indigo-600 hover:bg-indigo-50 flex items-center gap-3 font-medium"
              >
                {isIOS ? <Share className="w-4 h-4" /> : <Download className="w-4 h-4" />}
                <span>App installieren</span>
              </button>
            )}

            {isModerator && (
              <button
                onClick={(e) => handleAction('moderation', e)}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
              >
                <Shield className="w-4 h-4" />
                <span>Moderation</span>
                {pendingCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {pendingCount}
                  </span>
                )}
              </button>
            )}

            {isAdmin && (
              <button
                onClick={(e) => handleAction('admin', e)}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
              >
                <Crown className="w-4 h-4 text-yellow-500" />
                <span>Admin-Bereich</span>
              </button>
            )}

            {/* Über Akkordeon */}
            <div className="border-t border-gray-100 mt-1 pt-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAbout(!showAbout);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
              >
                <Info className="w-4 h-4" />
                <span>Über</span>
                {showAbout ? (
                  <ChevronUp className="w-4 h-4 ml-auto" />
                ) : (
                  <ChevronDown className="w-4 h-4 ml-auto" />
                )}
              </button>
              {showAbout && (
                <div className="bg-gray-50 py-1">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      // TODO: Navigate to About page or show modal
                      onClose();
                    }}
                    className="w-full px-4 py-2 pl-11 text-left text-sm text-gray-600 hover:bg-gray-100 flex items-center gap-3"
                  >
                    <Info className="w-4 h-4" />
                    <span>Über GoodPlace</span>
                  </a>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      // TODO: Navigate to Privacy page or show modal
                      onClose();
                    }}
                    className="w-full px-4 py-2 pl-11 text-left text-sm text-gray-600 hover:bg-gray-100 flex items-center gap-3"
                  >
                    <FileText className="w-4 h-4" />
                    <span>Datenschutz</span>
                  </a>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      // TODO: Navigate to Impressum page or show modal
                      onClose();
                    }}
                    className="w-full px-4 py-2 pl-11 text-left text-sm text-gray-600 hover:bg-gray-100 flex items-center gap-3"
                  >
                    <Scale className="w-4 h-4" />
                    <span>Impressum</span>
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Sign Out */}
          <div className="border-t border-gray-100 py-1">
            <button
              onClick={handleSignOut}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <LogOut className="w-4 h-4" />
              <span>Abmelden</span>
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Not logged in */}
          <button
            onClick={(e) => handleAction('login', e)}
            className="w-full px-4 py-2 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50 flex items-center gap-3"
          >
            <LogIn className="w-4 h-4" />
            <span>Anmelden</span>
          </button>
          <button
            onClick={(e) => handleAction('register', e)}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
          >
            <User className="w-4 h-4" />
            <span>Registrieren</span>
          </button>
          <div className="border-t border-gray-100 my-1" />
          <button
            onClick={(e) => handleAction('magazine', e)}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
          >
            <BookOpen className="w-4 h-4" />
            <span>Magazin</span>
          </button>
          {isMobile && isInstallable && !isInstalled && (
            <button
              onClick={() => {
                if (isIOS) {
                  alert('Tippe auf "Teilen" und dann auf "Zum Home-Bildschirm", um die App zu installieren.');
                } else {
                  promptInstall();
                }
                onClose();
              }}
              className="w-full px-4 py-2 text-left text-sm text-indigo-600 hover:bg-indigo-50 flex items-center gap-3 font-medium"
            >
              {isIOS ? <Share className="w-4 h-4" /> : <Download className="w-4 h-4" />}
              <span>App installieren</span>
            </button>
          )}

          {/* Über Akkordeon (nicht eingeloggt) */}
          <div className="border-t border-gray-100 mt-1 pt-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAbout(!showAbout);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <Info className="w-4 h-4" />
              <span>Über</span>
              {showAbout ? (
                <ChevronUp className="w-4 h-4 ml-auto" />
              ) : (
                <ChevronDown className="w-4 h-4 ml-auto" />
              )}
            </button>
            {showAbout && (
              <div className="bg-gray-50 py-1">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onClose();
                  }}
                  className="w-full px-4 py-2 pl-11 text-left text-sm text-gray-600 hover:bg-gray-100 flex items-center gap-3"
                >
                  <Info className="w-4 h-4" />
                  <span>Über GoodPlace</span>
                </a>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onClose();
                  }}
                  className="w-full px-4 py-2 pl-11 text-left text-sm text-gray-600 hover:bg-gray-100 flex items-center gap-3"
                >
                  <FileText className="w-4 h-4" />
                  <span>Datenschutz</span>
                </a>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onClose();
                  }}
                  className="w-full px-4 py-2 pl-11 text-left text-sm text-gray-600 hover:bg-gray-100 flex items-center gap-3"
                >
                  <Scale className="w-4 h-4" />
                  <span>Impressum</span>
                </a>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default UserMenu;
